import { useState, type KeyboardEvent, type PointerEvent } from 'react';
import { formatMonth } from '@/lib/activity';
import { pluralize } from '@/lib/format';
import type { ActivityPoint } from '@/types/api';
import { niceTicks, xLabelStep } from './chart-utils';
import { useContainerWidth } from './use-container-width';

/**
 * The one time-series chart of the statistics page, hand-rolled SVG so it follows the app's
 * theme tokens in both modes and costs no dependency. Two variants of the same monthly series:
 * `columns` (the per-month rate) and `cumulative` (the running total, as a 2px line over a wash).
 *
 * Interaction contract: hovering snaps to the nearest month and shows a tooltip; the same
 * readout is reachable from the keyboard (focus the chart, then arrow keys). Nothing is ONLY in
 * the tooltip — the parent renders a full table view of the series.
 */

/** Fixed vertical geometry. The SVG height always includes the x-axis band — no nested scroll. */
const PLOT_HEIGHT = 192;
const MARGIN_TOP = 20;
const MARGIN_BOTTOM = 28;
const BAR_MAX_WIDTH = 24;

interface MonthlyChartProps {
  /** Gap-filled series (see fillMonthGaps) — the axis trusts index adjacency = month adjacency. */
  points: ActivityPoint[];
  variant: 'columns' | 'cumulative';
  ariaLabel: string;
  /** What is counted, singular, for the tooltip ("3 links"). */
  unit?: string;
}

/** Bar with a 4px-rounded data end and a square baseline end. */
function roundedBarPath(x: number, y: number, width: number, height: number): string {
  const r = Math.min(4, width / 2, height);
  const bottom = y + height;
  return [
    `M ${x} ${bottom}`,
    `L ${x} ${y + r}`,
    `Q ${x} ${y} ${x + r} ${y}`,
    `L ${x + width - r} ${y}`,
    `Q ${x + width} ${y} ${x + width} ${y + r}`,
    `L ${x + width} ${bottom}`,
    'Z',
  ].join(' ');
}

export function MonthlyChart({ points, variant, ariaLabel, unit = 'link' }: MonthlyChartProps) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);

  const max = points.reduce((peak, point) => Math.max(peak, point.count), 0);
  const ticks = niceTicks(max);
  const topTick = ticks[ticks.length - 1] || 1;

  const lastCount = points.length > 0 ? points[points.length - 1].count : 0;
  const marginLeft = 16 + topTick.toLocaleString().length * 7;
  // The cumulative variant direct-labels its endpoint to the right of the last dot.
  const marginRight = variant === 'cumulative' ? 14 + lastCount.toLocaleString().length * 7 : 8;
  const innerWidth = Math.max(40, width - marginLeft - marginRight);
  const height = MARGIN_TOP + PLOT_HEIGHT + MARGIN_BOTTOM;
  const baseline = MARGIN_TOP + PLOT_HEIGHT;
  const bandWidth = innerWidth / Math.max(1, points.length);
  const barWidth = Math.min(BAR_MAX_WIDTH, Math.max(2, bandWidth * 0.7));

  const xCenter = (index: number) => marginLeft + (index + 0.5) * bandWidth;
  const yFor = (value: number) => MARGIN_TOP + PLOT_HEIGHT * (1 - value / topTick);

  const labelStride = xLabelStep(points.length, innerWidth);
  const isLabelled = (index: number) => (points.length - 1 - index) % labelStride === 0;

  // Direct-label only the extreme; the y-axis, tooltip and table carry every other value.
  const peakIndex = max > 0 ? points.findIndex((point) => point.count === max) : -1;

  const linePath = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${xCenter(index)} ${yFor(point.count)}`)
    .join(' ');
  const areaPath =
    points.length > 0
      ? `${linePath} L ${xCenter(points.length - 1)} ${baseline} L ${xCenter(0)} ${baseline} Z`
      : '';

  const indexFromPointer = (event: PointerEvent<SVGRectElement>): number | null => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg || points.length === 0) return null;
    const x = event.clientX - svg.getBoundingClientRect().left - marginLeft;
    return Math.min(points.length - 1, Math.max(0, Math.floor(x / bandWidth)));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (points.length === 0) return;
    const lastIndex = points.length - 1;
    let next: number | null;
    switch (event.key) {
      case 'ArrowRight':
        next = active === null ? lastIndex : Math.min(lastIndex, active + 1);
        break;
      case 'ArrowLeft':
        next = active === null ? lastIndex : Math.max(0, active - 1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = lastIndex;
        break;
      case 'Escape':
        next = null;
        break;
      default:
        return;
    }
    event.preventDefault();
    setActive(next);
  };

  const activePoint = active !== null ? points[active] : undefined;
  const activeY = activePoint ? yFor(activePoint.count) : 0;
  const tooltipLeft = active !== null ? Math.min(Math.max(xCenter(active), 56), width - 56) : 0;

  return (
    <div
      ref={ref}
      role="img"
      aria-label={ariaLabel}
      tabIndex={0}
      className="relative w-full rounded-md"
      onKeyDown={onKeyDown}
      onBlur={() => setActive(null)}
    >
      <svg width={width} height={height} aria-hidden="true" className="block">
        {/* Gridlines: solid hairlines one step off the surface; the zero line reads as baseline. */}
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={marginLeft}
              x2={marginLeft + innerWidth}
              y1={yFor(tick)}
              y2={yFor(tick)}
              stroke="hsl(var(--border))"
              strokeOpacity={tick === 0 ? 1 : 0.55}
              strokeWidth={1}
            />
            <text
              x={marginLeft - 8}
              y={yFor(tick) + 3.5}
              textAnchor="end"
              fontSize={11}
              fill="hsl(var(--muted-foreground))"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {tick.toLocaleString()}
            </text>
          </g>
        ))}

        {/* Month labels, thinned so they never collide, anchored to the most recent month. */}
        {points.map((point, index) =>
          isLabelled(index) ? (
            <text
              key={point.period}
              x={xCenter(index)}
              y={baseline + 18}
              textAnchor="middle"
              fontSize={11}
              fill="hsl(var(--muted-foreground))"
            >
              {formatMonth(point.period)}
            </text>
          ) : null,
        )}

        {variant === 'columns' ? (
          <g>
            {points.map((point, index) => {
              if (point.count <= 0) return null;
              const y = yFor(point.count);
              return (
                <path
                  key={point.period}
                  data-period={point.period}
                  d={roundedBarPath(xCenter(index) - barWidth / 2, y, barWidth, baseline - y)}
                  fill="hsl(var(--primary))"
                  fillOpacity={active === index ? 1 : 0.85}
                />
              );
            })}
            {peakIndex >= 0 ? (
              <text
                x={xCenter(peakIndex)}
                y={yFor(points[peakIndex].count) - 6}
                textAnchor="middle"
                fontSize={11}
                fontWeight={500}
                fill="hsl(var(--foreground))"
              >
                {points[peakIndex].count.toLocaleString()}
              </text>
            ) : null}
          </g>
        ) : (
          <g>
            <path d={areaPath} fill="hsl(var(--primary))" fillOpacity={0.1} />
            <path
              d={linePath}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {activePoint && active !== points.length - 1 ? (
              <g>
                {/* Crosshair snapped to the hovered month. */}
                <line
                  x1={xCenter(active ?? 0)}
                  x2={xCenter(active ?? 0)}
                  y1={MARGIN_TOP}
                  y2={baseline}
                  stroke="hsl(var(--border))"
                  strokeWidth={1}
                />
                <circle
                  cx={xCenter(active ?? 0)}
                  cy={activeY}
                  r={4}
                  fill="hsl(var(--primary))"
                  stroke="hsl(var(--card))"
                  strokeWidth={2}
                />
              </g>
            ) : null}
            {points.length > 0 ? (
              <g>
                {/* Endpoint: ≥8px dot with a surface ring, direct-labelled with the final total. */}
                <circle
                  cx={xCenter(points.length - 1)}
                  cy={yFor(lastCount)}
                  r={4}
                  fill="hsl(var(--primary))"
                  stroke="hsl(var(--card))"
                  strokeWidth={2}
                />
                <text
                  x={xCenter(points.length - 1) + 8}
                  y={yFor(lastCount) + 4}
                  fontSize={11}
                  fontWeight={500}
                  fill="hsl(var(--foreground))"
                >
                  {lastCount.toLocaleString()}
                </text>
              </g>
            ) : null}
          </g>
        )}

        {/* One transparent hit layer over the whole plot: the target is the band, not the mark. */}
        <rect
          x={marginLeft}
          y={MARGIN_TOP}
          width={innerWidth}
          height={PLOT_HEIGHT}
          fill="transparent"
          onPointerMove={(event) => setActive(indexFromPointer(event))}
          onPointerDown={(event) => setActive(indexFromPointer(event))}
          onPointerLeave={() => setActive(null)}
        />
      </svg>

      {activePoint ? (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-border bg-popover px-2.5 py-1.5 text-popover-foreground shadow-md"
          style={{
            left: tooltipLeft,
            top: Math.max(activeY - 10, 4),
            transform: 'translate(-50%, -100%)',
          }}
        >
          <p className="whitespace-nowrap text-sm font-semibold">
            {activePoint.count.toLocaleString()} {pluralize(activePoint.count, unit)}
            {variant === 'cumulative' ? ' total' : ''}
          </p>
          <p className="whitespace-nowrap text-xs text-muted-foreground">
            {formatMonth(activePoint.period)}
          </p>
        </div>
      ) : null}
    </div>
  );
}
